// Kam vede klepnutí na push notifikaci nebo na její tlačítko (úkol 2.6, docs/SPEC.md 2.8).
// Samostatný modul bez závislosti na service workeru, aby šel testovat.

export const LEAD_ACTIONS = ['call', 'propose', 'reject'] as const
export type LeadAction = (typeof LEAD_ACTIONS)[number]

export interface NotificationData {
  url?: string
  jobId?: string
}

const JOB_PATH = /^\/zakazky\/([^/?#]+)/

/** Id zakázky z cesty `/zakazky/<id>` (nová zakázka se nepočítá). */
export function jobIdFromUrl(url: string | undefined): string | null {
  const m = url ? JOB_PATH.exec(url) : null
  if (!m || m[1] === 'nova') return null
  return m[1] ?? null
}

/**
 * Cesta v aplikaci pro daný `action` tlačítka. Bez akce (nebo bez id zakázky) se otevře `url` jako dřív.
 * - call: detail s ?action=call (detail sám vyvolá tel: po načtení, kvůli oprávněním prohlížeče)
 * - propose: nová událost svázaná se zakázkou
 * - reject: detail s ?action=reject (otevře dialog Odmítnout)
 */
export function notificationTarget(
  action: string | undefined,
  data: NotificationData | undefined,
): string {
  const fallback = data?.url ?? '/'
  if (action !== 'call' && action !== 'propose' && action !== 'reject') return fallback
  const id = data?.jobId || jobIdFromUrl(data?.url)
  if (!id) return fallback
  // I „Navrhnout termín“ jde přes detail zakázky, aby se zapsala první odpověď (first_response_at).
  const safe = encodeURIComponent(id)
  return `/zakazky/${safe}?action=${action}`
}

/** Akce z push zprávy: jen známé, s textem, nejvýš tři. */
export function parseActions(raw: unknown): Array<{ action: LeadAction; title: string }> {
  if (!Array.isArray(raw)) return []
  const out: Array<{ action: LeadAction; title: string }> = []
  for (const item of raw) {
    const a = (item as { action?: unknown; title?: unknown } | null) ?? {}
    if (
      typeof a.action === 'string' &&
      (LEAD_ACTIONS as readonly string[]).includes(a.action) &&
      typeof a.title === 'string' &&
      a.title
    ) {
      out.push({ action: a.action as LeadAction, title: a.title })
    }
  }
  return out.slice(0, 3)
}

/** Jen cesta uvnitř aplikace: "//cizi.web" nebo "https://..." z push dat se zahodí. */
export function internalPath(url: unknown): string | null {
  if (typeof url !== 'string') return null
  if (!url.startsWith('/') || url.startsWith('//') || url.includes('\\')) return null
  return url
}

/**
 * Cíl klepnutí na nativní push (Capacitor): `data` z FCM/APNs, `actionId` je "tap" pro klepnutí na tělo.
 * Vrací bezpečnou cestu v aplikaci, nebo null, když zpráva žádný cíl nenese.
 */
export function nativeNotificationTarget(
  actionId: string | undefined,
  data: unknown,
): string | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as { url?: unknown; jobId?: unknown }
  const clean: NotificationData = {
    url: internalPath(raw.url) ?? undefined,
    jobId: typeof raw.jobId === 'string' && raw.jobId ? raw.jobId : undefined,
  }
  if (!clean.url && !clean.jobId) return null
  return internalPath(notificationTarget(actionId, clean))
}
